import { useEffect, useLayoutEffect, useState } from "react"

import Form from "@forento/ui/components/Form"
import Portal from "@forento/ui/components/Portal"
import { type FCC } from "@forento/ui/types/react"
import { cn } from "@forento/ui/utilities/tailwind"

type Props = { isOpen: boolean; onSubmit?: () => void; className?: string }
const Modal: FCC<Props> = ({ isOpen, onSubmit, className, children }) => {
	const [state, setState] = useState<"idle" | "open" | "closed">(isOpen ? "open" : "idle")

	useLayoutEffect(() => {
		if (!isOpen && state === "idle") return
		setState(isOpen ? "open" : "closed")
	}, [isOpen, state])

	useEffect(() => {
		if (state !== "closed") return

		const timeout = setTimeout(() => {
			setState("idle")
		}, 300)

		return () => clearTimeout(timeout)
	}, [state])

	if (state === "idle") return null

	const frameClasses = cn(
		"flex max-h-11/12 w-full max-w-5xl scale-0 flex-col overflow-y-auto rounded-lg bg-white px-6 py-4 transition-transform",
		isOpen && "scale-100 starting:scale-0",
	)

	return (
		<Portal>
			<div
				className={cn(
					"pointer-events-none fixed inset-0 z-50 flex items-center justify-center bg-transparent transition",
					isOpen && "pointer-events-auto bg-black/50 opacity-100 backdrop-blur-sm starting:bg-transparent",
				)}
			>
				{onSubmit ? (
					<Form onSubmit={onSubmit} className={cn(frameClasses, className)}>
						{children}
					</Form>
				) : (
					<div className={cn(frameClasses, className)}>{children}</div>
				)}
			</div>
		</Portal>
	)
}

export default Modal
